import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

/**
 * Converts HTML content to plain text, preserving line breaks
 */
export function htmlToPlainText(html: string): string {
    if (!html) return '';

    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Function to recursively extract text with line breaks
    function extractTextWithBreaks(element: Element): string {
        let result = '';

        for (const child of Array.from(element.childNodes)) {
            if (child.nodeType === Node.TEXT_NODE) {
                result += child.textContent || '';
            } else if (child.nodeType === Node.ELEMENT_NODE) {
                const el = child as Element;
                const tagName = el.tagName.toLowerCase();

                // Handle <br> tags
                if (tagName === 'br') {
                    result += '\n';
                }
                // Handle block elements that should create line breaks
                else if (['div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'li'].includes(tagName)) {
                    const innerText = extractTextWithBreaks(el);
                    if (innerText.trim()) {
                        result += innerText + '\n';
                    } else {
                        result += '\n';
                    }
                }
                // Handle other elements
                else {
                    result += extractTextWithBreaks(el);
                }
            }
        }

        return result;
    }

    const text = extractTextWithBreaks(tempDiv);

    // Clean up extra whitespace and normalize line breaks
    return text
        .replace(/\r\n/g, '\n') // Normalize Windows line breaks
        .replace(/\r/g, '\n')   // Normalize old Mac line breaks
        .trim();
}

/**
 * Converts plain text with line breaks to HTML for display
 */
export function plainTextToHtml(text: string): string {
    if (!text) return '';

    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/\n/g, '<br>');
}